// Supabase removed - using API calls instead
import { buildApiUrl, secureFetch } from "@/lib/queryClient";

export type NoteGroupingMode = 'session-lesson' | 'daily';

export interface NoteSource {
  type: 'session' | 'lesson' | 'daily';
  id: string;
  name: string;
}

/**
 * Get the current grouping mode from localStorage
 */
export const getNoteGroupingMode = (): NoteGroupingMode => {
  const mode = localStorage.getItem('noteGroupingMode');
  return (mode as NoteGroupingMode) || 'session-lesson';
};

/**
 * Set the note grouping mode
 */
export const setNoteGroupingMode = (mode: NoteGroupingMode) => {
  localStorage.setItem('noteGroupingMode', mode);
};

/**
 * Determine the note source based on metadata and grouping mode
 */
export const determineNoteSource = (
  metadata?: {
    source?: 'session' | 'lesson';
    session_id?: string;
    session_name?: string;
    lesson_id?: string;
    lesson_title?: string;
  }
): NoteSource => {
  const mode = getNoteGroupingMode();

  if (mode === 'daily') {
    const today = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    return {
      type: 'daily',
      id: new Date().toISOString().split('T')[0], // YYYY-MM-DD
      name: today
    };
  }

  // Session-lesson mode
  if (metadata?.source === 'session' && metadata.session_id) {
    return {
      type: 'session',
      id: metadata.session_id,
      name: metadata.session_name || 'Chat Session'
    };
  }

  if (metadata?.source === 'lesson' && metadata.lesson_id) {
    return {
      type: 'lesson',
      id: metadata.lesson_id,
      name: metadata.lesson_title || 'Lesson'
    };
  }

  // Default to daily if no metadata
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  return {
    type: 'daily',
    id: new Date().toISOString().split('T')[0],
    name: today
  };
};

/**
 * Find or create a note for the given source
 */
export const findOrCreateNoteForSource = async (
  userId: string,
  source: NoteSource,
  initialContent?: string
): Promise<string> => {
  try {
    const response = await secureFetch(buildApiUrl('/api/notes/find-or-create'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sourceType: source.type,
        sourceId: source.id,
        sourceName: source.name,
      }),
    });

    if (!response.ok) {
      throw new Error('Error creating note');
    }

    const { id: noteId } = await response.json();

    // Generate AI title for new note if we have content
    if (noteId && initialContent) {
      await generateAndUpdateNoteTitle(noteId, initialContent, source);
    }

    return noteId;
  } catch (error) {
    console.error('Error finding or creating note:', error);
    throw error;
  }
};

/**
 * Generate an AI-powered title for a note via backend API
 */
export const generateAndUpdateNoteTitle = async (
  noteId: string,
  content: string,
  source: NoteSource
): Promise<void> => {
  try {
    const response = await secureFetch(buildApiUrl(`/api/notes/${noteId}/generate-title`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content,
        sourceType: source.type,
        sourceName: source.name,
      }),
    });

    if (!response.ok) {
      console.warn('Failed to generate AI title - continuing with default');
    }
  } catch (error) {
    console.error('Failed to generate AI title:', error);
  }
};

/**
 * Extract text content from Tiptap JSON
 */
export const extractTextFromTiptapContent = (content: string): string => {
  try {
    const parsed = JSON.parse(content);
    const extractText = (node: any): string => {
      if (node.type === 'text') return node.text || '';
      if (node.content) {
        return node.content.map(extractText).join(' ');
      }
      return '';
    };
    return extractText(parsed).substring(0, 500);
  } catch {
    return content.substring(0, 500);
  }
};

/**
 * Daily Trail grouping types
 */
export type DailyTrailGroupKey = 'today' | 'yesterday' | 'thisWeek' | 'earlier';

export interface DailyTrailGroup<T> {
  key: DailyTrailGroupKey;
  label: string;
  notes: T[];
}

/**
 * Get the start of day for a given date in local timezone
 */
const getStartOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the start of week (Sunday) for a given date
 */
const getStartOfWeek = (date: Date): Date => {
  const result = getStartOfDay(date);
  const day = result.getDay();
  result.setDate(result.getDate() - day);
  return result;
};

/**
 * Determine which Daily Trail bucket a date belongs to
 */
const getDayTrailBucket = (createdAt: Date, now: Date): DailyTrailGroupKey => {
  const today = getStartOfDay(now);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const startOfWeek = getStartOfWeek(now);
  
  const noteDay = getStartOfDay(createdAt);
  
  if (noteDay.getTime() === today.getTime()) {
    return 'today';
  }
  
  if (noteDay.getTime() === yesterday.getTime()) {
    return 'yesterday';
  }
  
  if (noteDay >= startOfWeek && noteDay < yesterday) {
    return 'thisWeek';
  }
  
  return 'earlier';
};

/**
 * Get display label for a Daily Trail group
 */
const getGroupLabel = (key: DailyTrailGroupKey): string => {
  switch (key) {
    case 'today': return 'Today';
    case 'yesterday': return 'Yesterday';
    case 'thisWeek': return 'This week';
    case 'earlier': return 'Earlier';
  }
};

/**
 * Group notes by Daily Trail buckets (Today, Yesterday, This Week, Earlier)
 * @param notes Array of notes with created_at timestamps
 * @param now Current date (for testing and timezone consistency)
 * @returns Array of non-empty groups with notes sorted by recency within each group
 */
export const groupNotesByDayTrail = <T extends { created_at: string }>(
  notes: T[],
  now: Date = new Date()
): DailyTrailGroup<T>[] => {
  const groups: Record<DailyTrailGroupKey, T[]> = {
    today: [],
    yesterday: [],
    thisWeek: [],
    earlier: [],
  };
  
  for (const note of notes) {
    const createdAt = new Date(note.created_at);
    const bucket = getDayTrailBucket(createdAt, now);
    groups[bucket].push(note);
  }
  
  for (const key of Object.keys(groups) as DailyTrailGroupKey[]) {
    groups[key].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }
  
  const orderedKeys: DailyTrailGroupKey[] = ['today', 'yesterday', 'thisWeek', 'earlier'];
  
  return orderedKeys
    .filter(key => groups[key].length > 0)
    .map(key => ({
      key,
      label: getGroupLabel(key),
      notes: groups[key],
    }));
};
